import type { WorkspaceSpotlightTourStep } from '../../../shared/ui/common/WorkspaceSpotlightTour';
import { quickFillLayout } from '../../../theme/theme';
import {
  detail,
  spotlightStep,
  type QuickFillTourContentArgs,
} from './quickFillGuideHelpers';
import {
  buildFindReplacePreviewTourStep,
  buildSaveReviewTourStep,
} from './quickFillDialogPreviewTourSteps';
import { buildAutoFillPreviewTourStep, buildFooterActionsTourStep } from './quickFillApplyTourSteps';
import {
  buildValuePanelTourStep,
  buildValueSearchTourStep,
} from './quickFillValuePanelTourSteps';

export { buildQuickFillGuideContent } from './quickFillGuideCardContent';
export type { QuickFillTourRefs } from './quickFillGuideHelpers';

export const buildQuickFillTourSteps = ({
  rowCount,
  refs,
  hostRibbonAvailable = false,
}: QuickFillTourContentArgs): WorkspaceSpotlightTourStep[] => [
  spotlightStep(
    {
      id: 'page-guide',
      title: 'Reopen the Quick Fill guide',
      focusLabel: 'Quick Fill guide controls',
      spotlightPadding: 5,
      intro: 'Use these controls to review guidance on editing scope, bulk changes, recovery, and next steps without leaving the table.',
      details: [
        detail('Choose the level of detail', [
          'Open the Page Guide to review the complete Revit parameter-editing workflow.',
          'Use the nearby Help entry for a brief reminder that Mapping defines how Revit data maps to PCF output and Validation confirms the result.',
        ]),
        detail('Return to the guidance when needed', [
          'You can reopen the Page Guide whenever the intended row, cell, or column scope is unclear.',
        ], 'accent'),
      ],
      nextAction: 'Reopen the Page Guide before editing if you are unsure which Revit rows and parameter columns should change.',
      chromeTargetId: 'quickfill-guide',
    },
    refs.pageGuideRef,
    {
      top: 0,
      left: Math.max(window.innerWidth - 120, 0),
      width: 120,
      height: 40,
    }
  ),
  ...(!hostRibbonAvailable
    ? [
        spotlightStep(
          {
            id: 'undo-recovery',
            title: 'Undo the wrong edit before continuing',
            focusLabel: 'Quick Fill edit recovery controls',
            spotlightPadding: 5,
            intro: 'Recovery controls reverse compatible Quick Fill sessions when the edited rows, cells, columns, or values do not match the intended Revit scope.',
            details: [
              detail('Recover promptly', [
                'Undo the latest compatible session as soon as you find an unintended row, cell, column, or value change.',
                'If connector setup created Revit parameters, use its recovery control before correcting the setup and retrying.',
              ]),
              detail('Do not delay', [
                'Recover before another save or bulk edit; later sessions can make the earlier snapshot incompatible.',
              ], 'caution'),
            ],
            nextAction: 'Undo now if the grid shows any change outside the intended Revit elements and parameter cells.',
          },
          refs.recoveryControlsRef,
          {
            top: 0,
            left: Math.max(window.innerWidth - 160, 0),
            width: 40,
            height: 40,
          }
        ),
      ]
    : []),
  spotlightStep(
    {
      id: 'action-bar',
      title: 'Set the edit scope from the action bar',
      focusLabel: 'Quick Fill scope and edit commands',
      intro: 'Use the action bar to control row mode, visible parameter columns, filters, selections, bulk-edit tools, and CSV import/export.',
      details: [
        detail('Set the scope first', [
          'Choose family/type or instance rows before selecting rows or cells.',
          'Use Manage columns and header filters to show only the identifiers, target parameters, and rows needed for the current engineering case.',
          'Use cell tools, Find and Replace, Auto-fill, or CSV only after the intended rows and columns are clear.',
        ]),
        detail('Keep mapping separate', [
          'Do not use Quick Fill editing tools to compensate for an incorrect PCF mapping, formula, fallback, or SKEY rule.',
        ], 'caution'),
      ],
      nextAction: 'Set the row mode, visible columns, and filters, then use the most targeted command that affects only the intended cells.',
    },
    refs.actionBarRef,
    {
      top: 112,
      left: 32,
      width: Math.max(window.innerWidth - 64, 320),
      height: 88,
    }
  ),
  spotlightStep(
    {
      id: 'row-mode',
      title: 'Choose what each row represents',
      focusLabel: 'Revit family/type or instance row mode',
      spotlightPadding: 6,
      intro: 'Row mode determines whether each table row represents a shared family/type value or an individual placed Revit instance.',
      details: [
        detail('Choose the appropriate row mode', [
          'Use family/type rows when all components of the same type share the same Revit parameter value.',
          'Use instance rows when each placed component can have a different value.',
        ]),
        detail('Review the scope after switching', [
          'Changing the row mode changes which rows are shown and which Revit elements can be edited. Review the filters and selection before making changes.',
        ], 'caution'),
      ],
      nextAction: 'Choose the row mode that matches the Revit parameter binding, then select the rows you want to edit.',
    },
    refs.rowModeToggleRef,
    {
      top: 118,
      left: Math.max(window.innerWidth * 0.32, 240),
      width: 120,
      height: 46,
    }
  ),
  spotlightStep(
    {
      id: 'auto-fill-controls',
      title: 'Open Auto-fill for repeated empty cells',
      focusLabel: 'Auto-fill command',
      intro: 'Auto-fill suggests Revit parameter values for empty cells based on detected naming patterns. It does not change how those parameters map to PCF output.',
      details: [
        detail('Prepare the selection', [
          'Filter the table to the intended rows and make sure the target parameter column is visible before opening Auto-fill.',
        ]),
        detail('Require a consistent pattern', [
          'Reject the suggested changes if the same value would be applied across rows or parameters with different engineering requirements.',
        ], 'accent'),
      ],
      nextAction: 'Use Auto-fill only when the selected cells should follow the same known value pattern.',
    },
    refs.autoFillControlsRef,
    {
      top: 118,
      left: Math.max(window.innerWidth * 0.42, 200),
      width: 160,
      height: 56,
    }
  ),
  spotlightStep(
    {
      id: 'utility-tools',
      title: 'Copy or extend known cell values',
      focusLabel: 'Selected-cell copy and fill commands',
      intro: 'Use copy, paste, and fill commands to apply existing values to selected Revit parameter cells without changing the PCF mapping.',
      details: [
        detail('Match the selection', [
          'Copy and paste values only when the source and destination cells represent the same parameter and compatible rows.',
          'Fill down extends one parameter value across rows. Fill right extends values across columns, so make sure each target column can use the same engineering value.',
        ]),
      ],
      nextAction: 'Select the exact destination cells, then copy or fill only when the row and column context is consistent.',
    },
    refs.utilityToolsRef,
    {
      top: 118,
      left: Math.max(window.innerWidth - 420, 24),
      width: 400,
      height: 56,
    }
  ),
  spotlightStep(
    {
      id: 'model-selection',
      title: 'Verify selected rows in Revit',
      focusLabel: 'Grid-to-Revit component selection',
      spotlightPadding: 6,
      intro: 'Highlight the Revit components represented by the selected rows so you can verify them in the model before applying changes.',
      details: [
        detail('Verify the selected components', [
          'Select the rows, highlight the corresponding Revit components, and confirm their system, location, type, and instance.',
          'Use this check before instance edits or whenever similar row labels may represent different physical components.',
        ]),
        detail('Availability', [
          'The command requires selected rows that correspond to Revit elements and an active connection to Revit.',
        ], 'accent'),
      ],
      nextAction: 'Highlight the selected rows in Revit, then remove any component that should not be included in the edit.',
    },
    refs.modelSelectRef,
    {
      top: 118,
      left: Math.max(window.innerWidth - 260, 24),
      width: 46,
      height: 46,
    }
  ),
  spotlightStep(
    {
      id: 'csv-transfer',
      title: 'Edit large data sets through CSV',
      focusLabel: 'Quick Fill CSV export and import',
      spotlightPadding: 6,
      intro: 'Export the current Quick Fill table to CSV for bulk editing, then import the edited values back into matching Revit rows.',
      details: [
        detail('Keep rows aligned', [
          'Export only the rows and parameter columns you intend to edit.',
          'Import a CSV derived from the same Revit data, then review all matched, skipped, and changed rows.',
        ]),
        detail('Do not create rows', [
          'Quick Fill skips CSV rows that do not match existing Revit data. CSV import cannot create model elements or change PCF mappings.',
        ], 'caution'),
      ],
      nextAction: 'Import the edited CSV, review the matched rows and changed cells, then save only the intended changes to Revit.',
    },
    refs.csvToolsRef,
    {
      top: 118,
      left: Math.max(window.innerWidth - 120, 24),
      width: 86,
      height: 46,
    }
  ),
  spotlightStep(
    {
      id: 'find-replace',
      title: 'Open Find and Replace for repeated text',
      focusLabel: 'Find and Replace command',
      intro: 'Find and Replace searches for repeated text in the selected Revit parameter cells and lets you review the matches before making changes.',
      details: [
        detail('Prepare the search', [
          'Set the row filters and select the target parameter column before opening Find and Replace.',
        ]),
        detail('Review the controls', [
          'The next step shows a read-only preview of the dialog. In actual use, no values are changed until you apply the parameter value replacement.',
        ], 'accent'),
      ],
      nextAction: 'Confirm the intended rows and columns, then open Find and Replace and review the matches.',
    },
    refs.findReplaceRef,
    {
      top: 118,
      left: Math.max(window.innerWidth * 0.58, 280),
      width: 140,
      height: 56,
    }
  ),
  buildFindReplacePreviewTourStep(refs),
  spotlightStep(
    {
      id: 'navigator',
      title: 'Navigate the active filtered rows',
      focusLabel: 'Filtered Revit row position and markers',
      intro: 'The navigator shows your position within the filtered rows and any saved markers. Moving through the table does not change the current selection.',
      details: [
        detail('Move without losing context', [
          'Click the minimap to move to another part of the filtered row set.',
          'Check your current position before selecting or editing cells.',
          'Mark exceptions that require separate review or a different value.',
        ]),
        detail('Keep track of exceptions', [
          'Use markers to keep important rows easy to find when related items are far apart in the table.',
        ], 'accent'),
        detail('Selection remains separate', [
          'Moving through the table does not select rows or limit column-wide actions. Review the current selection before editing.',
        ], 'caution'),
      ],
      nextAction: 'Move to the next relevant row, confirm the current selection, and mark any exceptions before editing.',
    },
    refs.navigationDockRef,
    {
      top: Math.max(window.innerHeight - 344, 188),
      left: Math.max(window.innerWidth - 408, 24),
      width: 384,
      height: 300,
    }
  ),
  spotlightStep(
    {
      id: 'table',
      title: 'Confirm rows, cells, and columns in the table',
      focusLabel: 'Active Quick Fill table',
      intro: `The table contains ${rowCount} Revit row${rowCount === 1 ? '' : 's'} before filtering. Visible rows, selected cells, and parameter columns determine which values can be edited.`,
      details: [
        detail('Review the selection', [
          'Rows represent family/types or instances, cells contain individual parameter values, and columns represent Revit parameters.',
          'Confirm the filters, selection, editable cells, and row status before applying any bulk action.',
        ]),
        detail('Handle different values separately', [
          'If the same value is not correct for every selected cell, narrow the rows or columns and handle exceptions separately.',
        ], 'caution'),
      ],
      nextAction: 'Confirm exactly which rows and parameter cells should change, then proceed only when the table selection matches your intended edit.',
    },
    refs.tableViewportRef,
    {
      top: 212,
      left: 24,
      width: Math.max(window.innerWidth * 0.54, 340),
      height: Math.max(window.innerHeight - 320, 220),
    }
  ),
  spotlightStep(
    {
      id: 'table-editing-shortcuts',
      title: 'Edit exceptions and selections in the table',
      focusLabel: 'Quick Fill cell and row edit controls',
      intro: 'Direct editing changes a single cell, while selected-cell, selected-row, and active-column commands can update multiple Revit parameter values at once.',
      details: [
        detail('Choose the smallest scope', [
          'Edit a single cell for an exception. Use row selections or cell ranges only when the same value applies across the entire selection.',
          'Use the context menu for cut, copy, paste, fill, clear, Find and Replace, and Auto-fill.',
        ]),
        detail('Review before saving', [
          'Sorting and resizing change only the current table view, not the Revit data. Edited and failed states, together with Undo and Redo, help you review changes made during the current session.',
        ], 'accent'),
      ],
      nextAction: 'Use single-cell editing for exceptions and selection-based commands only when the same value applies to all selected cells.',
    },
    refs.tableViewportRef,
    {
      top: 212,
      left: 24,
      width: Math.max(window.innerWidth * 0.54, 340),
      height: Math.max(window.innerHeight - 320, 220),
    }
  ),
  spotlightStep(
    {
      id: 'advanced-filters',
      title: 'Filter rows before selecting cells',
      focusLabel: 'SKEY column filter',
      allowTargetInteraction: true,
      spotlightPadding: 4,
      intro: 'The SKEY column filter narrows the visible rows for review and selection. It does not change how SKEY is mapped.',
      relatedTargets: [
        {
          label: 'Open SKEY filter menu',
          visualLabel: 'Open SKEY filter menu',
          targetRef: refs.filterPopoverRef,
          fallbackRect: {
            top: 156,
            left: 228,
            width: 332,
            height: 360,
          },
        },
      ],
      details: [
        detail('Narrow the table', [
          'Open the SKEY column filter and choose Values to filter by existing entries or Condition to match a pattern.',
          'Review the remaining row identifiers before selecting cells or applying a column-wide action.',
        ]),
        detail('Keep filtering and editing separate', [
          'Filtering changes only which rows are visible. The selected cells and chosen edit command determine which values are changed.',
        ], 'accent'),
        detail('Check the engineering meaning', [
          'Rows with the same SKEY value do not necessarily require the same edited value.',
        ], 'caution'),
      ],
      nextAction: 'Apply the filter, review the remaining Revit rows, then select the exact cells you intend to edit.',
    },
    refs.filterTriggerRef,
    {
      top: 152,
      left: Math.min(Math.max(window.innerWidth * 0.3, 220), window.innerWidth - 80),
      width: 38,
      height: 38,
    }
  ),
  spotlightStep(
    {
      id: 'columns',
      title: 'Manage columns controls the review view',
      focusLabel: 'Row identifiers and editable parameter fields',
      compactOnNarrowViewport: true,
      intro: 'Manage columns lets you choose which row identifiers and editable parameter fields are shown in the table.',
      relatedTargets: [
        {
          label: 'Manage columns control',
          targetRef: refs.manageColumnsRef,
        },
      ],
      details: [
        detail('How to use it', [
          'Show the fields needed to identify rows and review or edit values.',
          'Hide unrelated columns to reduce clutter.',
          'Use search when the column list is long.',
        ]),
        detail('Why it matters', [
          'Keeping only relevant columns visible makes bulk edits easier to review.',
        ], 'accent'),
        detail('Scope and context', [
          'Identifier columns help confirm which families/types or instances are being edited.',
          'Editable parameter columns contain the values that can be changed. Keep the relevant identifiers visible while editing.',
        ], 'caution'),
      ],
      nextAction: 'Keep the relevant identifier columns visible together with the parameter fields needed for the current edit.',
    },
    refs.propertyPanelRef,
    {
      top: 118,
      left: Math.max(window.innerWidth - 364, 24),
      width: quickFillLayout.propertySpotlightWidth,
      height: Math.max(window.innerHeight - 250, 280),
    }
  ),
  spotlightStep(
    {
      id: 'column-panel-search',
      title: 'Search inside Manage columns',
      focusLabel: 'Parameter search in Manage columns',
      intro: 'When many parameters are available, use Search Columns to narrow the list before choosing which columns to show.',
      relatedTargets: [
        {
          label: 'Manage columns panel',
          targetRef: refs.propertyPanelRef,
        },
      ],
      details: [
        detail('How to use it', [
          'Type part of a column or parameter name instead of scrolling through the full list.',
        ]),
      ],
      nextAction: 'When the column list is long, search first, then show only the fields needed for the current edit.',
    },
    refs.propertySearchRef,
    {
      top: 152,
      left: Math.max(window.innerWidth - 348, 32),
      width: 300,
      height: 52,
    }
  ),
  buildValuePanelTourStep(refs),
  buildValueSearchTourStep(refs),
  buildAutoFillPreviewTourStep(refs),
  buildSaveReviewTourStep(refs),
  buildFooterActionsTourStep(refs),
];
